import React, { useState, useEffect, useCallback, memo, useMemo } from 'react'
import {
  Box,
  Button,
  Typography,
  Snackbar,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Autocomplete,
  TextField,
  InputAdornment,
  IconButton,
  Popover,
  Chip,
  Tooltip,
} from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import ClearIcon from '@mui/icons-material/Clear'
import CustomTextField from '../CustomTextField'
import apiService from '../../services/ApiService'
import { activityTracker } from '../../services/ActivityTracker'
import { getDomainInfo, getCountryFromDomain } from '../../utils/domainUtils'
import useAutocompleteRequestKey from '../../hooks/useAutocompleteRequestKey'
/**
 * TaxTreatySidebar component for filtering and searching tax treaties
 */
const TaxTreatySideBar = memo(
  ({
    // Country props
    primaryCountry,
    setPrimaryCountry,
    selectedCountry,
    setSelectedCountry,
    searchQuery,
    setSearchQuery,

    // Data sources
    availableCountries = [],

    // UI state
    loading = false,
    error = null,
    isAuthenticated = false,

    // Handler functions
    handleReset,
    handleSearch,

    // Navigation and pagination
    navigate,
    setCurrentPage,
    setCurrentPageData,
    setTotalItems,
    useExactSearch = false,
    autocompleteEnabled = true,
  }) => {
    const theme = useTheme()
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
    const domainInfo = getDomainInfo()
    const showCountrySelector = domainInfo.shouldShowCountrySelector
    // Local state
    const [partnerCountries, setPartnerCountries] = useState([])
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState('info')
    const [tempSearchQuery, setTempSearchQuery] = useState(searchQuery || '')
    const [selectedAutocompleteSuggestion, setSelectedAutocompleteSuggestion] =
      useState('')
    const [autocompleteResults, setAutocompleteResults] = useState({})
    const [autocompleteLoading, setAutocompleteLoading] = useState(false)
    const [showAutocomplete, setShowAutocomplete] = useState(false)
    const [isLoadingPartners, setIsLoadingPartners] = useState(false)
    const [popoverAnchor, setPopoverAnchor] = useState(null)
    const [popoverOpen, setPopoverOpen] = useState(false)
    const autocompleteContext = useMemo(
      () => ({
        primaryCountry,
        partnerCountry: selectedCountry,
      }),
      [primaryCountry, selectedCountry],
    )
    const {
      debouncedQuery: debouncedAutocompleteQuery,
      requestKey: autocompleteRequestKey,
    } = useAutocompleteRequestKey({
      query: tempSearchQuery,
      context: autocompleteContext,
      debounceMs: 300,
      minLength: 2,
    })
    // Format countries for Autocomplete
    const formattedCountries = useMemo(
      () =>
        availableCountries
          .filter((country) => country.countrySlug !== 'gcc') // Filter out GCC
          .map((country) => ({
            label: country.countryName,
            code: country.countryCode || 'xx',
            id: country.countrySlug || country.id || country.countryName,
            flagCode: country.countryCode?.toLowerCase() || 'xx',
            ...country,
          })),
      [availableCountries],
    )

    // Format partner countries for Autocomplete
    const formattedPartnerCountries = partnerCountries.map((country) => ({
      label: country.country2Name,
      code: country.country2Code || 'xx',
      id: country.country2Slug || country.id || country.country2Name,
      flagCode: country.country2Code?.toLowerCase() || 'xx',
      ...country,
    }))

    // Add empty option for "All Partner Countries"
    const partnerCountryOptions = [
      { label: 'All Partner Countries', code: '', id: '', flagCode: '' },
      ...formattedPartnerCountries,
    ]

    // UI conditions for disabled controls
    const isPrimaryCountryDisabled = loading || formattedCountries.length === 0
    const isPartnerCountryDisabled =
      !primaryCountry || loading || isLoadingPartners
    const isSearchButtonDisabled = !primaryCountry || loading
    const isSearchQueryDisabled = !isAuthenticated || loading

    // Theme colors for consistent styling
    const themeColors = {
      primary: '#232536',
      secondary: '#ffcf51',
      hover: '#3a3e5a',
      background: '#f8f8f8',
      border: '#e0e0e0',
    }

    /**
     * Handle closing the snackbar
     */
    const handleSnackbarClose = useCallback((event, reason) => {
      if (reason === 'clickaway') return
      setSnackbarOpen(false)
    }, [])

    /**
     * Show a snackbar message with specified severity
     */
    const showSnackbar = useCallback((message, severity = 'info') => {
      setSnackbarMessage(message)
      setSnackbarSeverity(severity)
      setSnackbarOpen(true)
    }, [])

    /**
     * Handle search button click
     */
    const handleSearchButtonClick = useCallback(() => {
      // Update the parent's searchQuery with our temp value
      setSearchQuery(tempSearchQuery)

      // Show search feedback with proper country names
      const message = `Searching "${
        tempSearchQuery || 'all content'
      }" in Tax Treaties between ${primaryCountry} ${
        selectedCountry ? `and ${selectedCountry}` : 'and all partner countries'
      }`

      // Call the parent's handleSearch function
      handleSearch(tempSearchQuery, selectedAutocompleteSuggestion || null)
      setShowAutocomplete(false)

      showSnackbar(message)
    }, [
      primaryCountry,
      selectedCountry,
      tempSearchQuery,
      selectedAutocompleteSuggestion,
      handleSearch,
      setSearchQuery,
      showSnackbar,
    ])

    /**
     * Handle reset button click
     */
    const handleResetClick = useCallback(() => {
      // Reset only the search query, not the selected countries
      setTempSearchQuery('')
      setSelectedAutocompleteSuggestion('')
      setAutocompleteResults({})
      setShowAutocomplete(false)
      setSearchQuery('')
      handleReset()
      showSnackbar('Filters have been reset')
    }, [handleReset, setSearchQuery, showSnackbar])

    /**
     * Handle primary country selection change
     */
    const handlePrimaryCountryChange = useCallback(
      (_, newValue) => {
        // Don't allow primary country changes on country-specific domains
        if (domainInfo.isCountrySpecific) {
          return
        }
        if (!newValue) return

        const newCountry = newValue.label

        if (isAuthenticated) {
          activityTracker.trackCustomEvent('tax_treaty_country1_changed', {
            from: primaryCountry,
            to: newCountry,
          })
        }

        setPrimaryCountry(newCountry)

        // Reset partner country selection
        setSelectedCountry('')

        // Reset search query
        setTempSearchQuery('')
        setSelectedAutocompleteSuggestion('')
        setAutocompleteResults({})
        setShowAutocomplete(false)
        setSearchQuery('')

        // Reset pagination
        if (typeof setCurrentPage === 'function') setCurrentPage(1)
        if (typeof setCurrentPageData === 'function') setCurrentPageData([])
        if (typeof setTotalItems === 'function') setTotalItems(0)

        // Update URL when primary country changes (skip when embedded in admin)
        if (navigate && !useExactSearch) {
          navigate(`/search/${newCountry}/Tax-Treaties`)
        }
      },
      [
        setPrimaryCountry,
        setSelectedCountry,
        setSearchQuery,
        navigate,
        setCurrentPage,
        setCurrentPageData,
        setTotalItems,
        domainInfo.isCountrySpecific,
        primaryCountry,
        isAuthenticated,
        useExactSearch,
      ],
    )

    /**
     * Handle partner country selection change
     */
    const handlePartnerCountryChange = useCallback(
      (_, newValue) => {
        const isAllPartnerSelection =
          !newValue || newValue.label === 'All Partner Countries'
        const newPartner = isAllPartnerSelection ? '' : newValue.label

        if (isAuthenticated && newPartner !== selectedCountry) {
          activityTracker.trackCustomEvent('tax_treaty_country2_changed', {
            from: selectedCountry || 'all',
            to: newPartner || 'all',
          })
        }

        setSelectedCountry(newPartner)
        setShowAutocomplete(false)
      },
      [setSelectedCountry, selectedCountry, isAuthenticated],
    )

    const handlePopoverOpen = (event) => {
      // Only show popover if user is not authenticated
      if (!isAuthenticated) {
        setPopoverAnchor(event.currentTarget)
        setPopoverOpen(true)
      }
    }

    const handlePopoverClose = () => {
      setPopoverOpen(false)
      setPopoverAnchor(null)
    }
    // Auto-select primary country on country-specific domains
    useEffect(() => {
      if (
        domainInfo.isCountrySpecific &&
        !primaryCountry &&
        availableCountries.length > 0
      ) {
        const countryFromDomain = getCountryFromDomain()
        if (countryFromDomain) {
          // Find the matching country in availableCountries
          const matchingCountry = availableCountries.find(
            (country) => country.countryName === countryFromDomain,
          )
          if (matchingCountry) {
            setPrimaryCountry(matchingCountry.countryName)
          }
        }
      }
    }, [
      domainInfo.isCountrySpecific,
      primaryCountry,
      availableCountries,
      setPrimaryCountry,
    ])
    // Fetch partner countries that have tax treaties with the primary country
    useEffect(() => {
      let isActive = true

      const fetchPartnerCountries = async () => {
        if (!primaryCountry) {
          setPartnerCountries([])
          return
        }

        setIsLoadingPartners(true)

        try {
          const data = await apiService.getTaxTreatyPartnerCountries(
            primaryCountry,
            {
              cancelPrevious: true,
              abortKey: `tax-treaty-partner-countries:${primaryCountry}`,
            },
          )
          if (isActive) {
            setPartnerCountries(data || [])
          }
        } catch (error) {
          if (apiService.isRequestCanceled(error) || error?.isCanceled) {
            return
          }
          console.error(
            `Error fetching partner countries for ${primaryCountry}:`,
            error,
          )
          const errorMessage =
            error && error.userMessage
              ? error.userMessage
              : 'Failed to load search results. Please try again.'
          setSnackbarMessage(errorMessage)
          setSnackbarSeverity('error')
          setSnackbarOpen(true)
          if (isActive) {
            setPartnerCountries([])
            showSnackbar(
              `Could not load partner countries for ${primaryCountry}`,
              'error',
            )
          }
        } finally {
          if (isActive) {
            setIsLoadingPartners(false)
          }
        }
      }

      fetchPartnerCountries()

      // Cleanup function to prevent state updates if component unmounts during request
      return () => {
        isActive = false
      }
    }, [primaryCountry, showSnackbar])

    // Update temp search query when parent search query changes
    useEffect(() => {
      setTempSearchQuery(searchQuery || '')
      setSelectedAutocompleteSuggestion('')
    }, [searchQuery])

    useEffect(() => {
      let cancelled = false
      if (!isAuthenticated || !autocompleteEnabled) {
        setAutocompleteResults({})
        setShowAutocomplete(false)
        setAutocompleteLoading(false)
        return undefined
      }

      const normalizedQuery = tempSearchQuery?.trim() || ''
      if (!normalizedQuery || !primaryCountry) {
        setAutocompleteResults({})
        setShowAutocomplete(false)
        setAutocompleteLoading(false)
        return undefined
      }

      const timer = setTimeout(async () => {
        try {
          setAutocompleteLoading(true)
          const response = await apiService.taxTreatySearchAutocompleteV2({
            q: normalizedQuery,
            country1: primaryCountry,
            country2: selectedCountry || '',
          })
          if (cancelled) return
          const safeResponse =
            response && typeof response === 'object' ? response : {}
          setAutocompleteResults(safeResponse)
          setShowAutocomplete(Object.keys(safeResponse).length > 0)
        } catch {
          if (cancelled) return
          setAutocompleteResults({})
          setShowAutocomplete(false)
        } finally {
          if (!cancelled) {
            setAutocompleteLoading(false)
          }
        }
      }, 300)

      return () => {
        cancelled = true
        clearTimeout(timer)
      }
    }, [tempSearchQuery, isAuthenticated, autocompleteEnabled, primaryCountry, selectedCountry])

    const autocompleteItems = useMemo(() => {
      const mappingPriority = {
        canonical: 4,
        explicit: 3,
        equivalent: 2,
        unknown: 1,
      }
      const dedupedByValue = new Map()
      const normalizedTypedQuery = (tempSearchQuery || '').trim().toLowerCase()

      Object.entries(autocompleteResults || {}).forEach(
        ([sourceTerm, value]) => {
          if (!Array.isArray(value)) return

          value.forEach((item) => {
            const normalizedSource = String(sourceTerm || '').toLowerCase()
            const fallbackMappingType = normalizedSource.includes('canonical')
              ? 'canonical'
              : 'unknown'

            const mappingType =
              typeof item === 'string'
                ? fallbackMappingType
                : (typeof item?.mappingType === 'string'
                    ? item.mappingType
                    : fallbackMappingType
                  ).toLowerCase()

            const candidates =
              typeof item === 'string'
                ? [item]
                : [
                    ...(Array.isArray(item?.input) ? item.input : []),
                    ...(Array.isArray(item?.synonyms) ? item.synonyms : []),
                  ]

            candidates.forEach((candidate) => {
              if (typeof candidate !== 'string') return
              const trimmedCandidate = candidate.trim()
              if (!trimmedCandidate) return

              const dedupeKey = trimmedCandidate.toLowerCase()
              const existing = dedupedByValue.get(dedupeKey)

              if (
                !existing ||
                (mappingPriority[mappingType] || 0) >
                  (mappingPriority[existing.mappingType] || 0)
              ) {
                dedupedByValue.set(dedupeKey, {
                  value: trimmedCandidate,
                  mappingType,
                  sourceTerm,
                })
              }
            })
          })
        },
      )

      return Array.from(dedupedByValue.values())
        .sort((a, b) => {
          const aExact = a.value.toLowerCase() === normalizedTypedQuery ? 0 : 1
          const bExact = b.value.toLowerCase() === normalizedTypedQuery ? 0 : 1
          if (aExact !== bExact) return aExact - bExact

          if (a.mappingType !== b.mappingType) {
            return (
              (mappingPriority[b.mappingType] || 0) -
              (mappingPriority[a.mappingType] || 0)
            )
          }
          return a.value.localeCompare(b.value, undefined, {
            sensitivity: 'base',
          })
        })
        .slice(0, 20)
    }, [autocompleteResults, tempSearchQuery])

    // Find selected country objects for Autocomplete
    const selectedPrimaryCountryOption =
      formattedCountries.find((option) => option.label === primaryCountry) ||
      null

    const selectedPartnerCountryOption =
      partnerCountryOptions.find(
        (option) => option.label === selectedCountry,
      ) || partnerCountryOptions[0]

    return (
      <Paper
        elevation={2}
        sx={{
          padding: '16px',
          backgroundColor: themeColors.background,
          borderRadius: '8px',
          height: 'fit-content',
          transition: 'box-shadow 0.3s ease',
          borderRight: '1px solid #ccc',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          },
        }}
      >
        {/* Header */}
        <Typography
          variant='h5'
          sx={{
            mb: 2,
            color: themeColors.primary,
            fontWeight: 500,
            letterSpacing: '0.02em',
          }}
        >
          Tax Treaty Filters
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* Error message display */}
        {error && (
          <Alert
            severity='error'
            sx={{
              mb: 2,
              boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Conditionally render Primary Country (Country 1) selection with Autocomplete */}

        {showCountrySelector ? (
          <Autocomplete
            size='small'
            id='country1-select'
            sx={{ mb: 2 }}
            options={formattedCountries}
            value={selectedPrimaryCountryOption}
            onChange={handlePrimaryCountryChange}
            autoHighlight
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={isPrimaryCountryDisabled}
            // disabled
            renderOption={(props, option) => {
              // Extract key from props to avoid warning
              const { key, ...optionProps } = props
              return (
                <Box
                  key={option.id}
                  component='li'
                  sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                  {...optionProps}
                >
                  {option.flagCode && (
                    <img
                      loading='lazy'
                      width='20'
                      srcSet={`https://flagcdn.com/w40/${option?.flagCode.toLowerCase()}.png 2x`}
                      src={`https://flagcdn.com/w20/${option?.flagCode.toLowerCase()}.png`}
                      alt=''
                    />
                  )}
                  {option.label}
                </Box>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Select Country 1'
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password', // disable autocomplete
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: themeColors.primary,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: themeColors.primary,
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: themeColors.border,
                    },
                    '&:hover fieldset': {
                      borderColor: themeColors.primary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: themeColors.primary,
                    },
                  },
                }}
              />
            )}
          />
        ) : (
          // Show primary country as read-only chip on country-specific domains
          <Box sx={{ mb: 2 }}>
            <Typography
              variant='body2'
              sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}
            >
              Country 1
            </Typography>
            <Chip
              label={primaryCountry}
              variant='outlined'
              sx={{
                borderColor: themeColors.primary,
                color: themeColors.primary,
                backgroundColor: '#f5f5f5',
                fontWeight: 500,
                height: 40,
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: '#f5f5f5', // Prevent hover effects
                },
              }}
            />
          </Box>
        )}

        {/* Partner Country (Country 2) selection with Autocomplete */}
        <Autocomplete
          size='small'
          id='country2-select'
          sx={{ mb: 2 }}
          options={partnerCountryOptions}
          value={selectedPartnerCountryOption}
          onChange={handlePartnerCountryChange}
          autoHighlight
          loading={isLoadingPartners}
          getOptionLabel={(option) => option.label || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={isPartnerCountryDisabled}
          renderOption={(props, option) => {
            // Skip flag for "All Partner Countries"
            if (!option.code) {
              const { key, ...optionProps } = props
              return (
                <Box key='all-partners' component='li' {...optionProps}>
                  {option.label}
                </Box>
              )
            }

            // Extract key from props to avoid warning
            const { key, ...optionProps } = props
            return (
              <Box
                key={option.id}
                component='li'
                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                {...optionProps}
              >
                {option.flagCode && (
                  <img
                    loading='lazy'
                    width='20'
                    srcSet={`https://flagcdn.com/w40/${option?.flagCode?.toLowerCase()}.png 2x`}
                    src={`https://flagcdn.com/w20/${option?.flagCode?.toLowerCase()}.png`}
                    alt=''
                  />
                )}
                {option.label}
              </Box>
            )
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Select Country 2'
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingPartners ? (
                      <CircularProgress size={20} color='inherit' />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password', // disable autocomplete
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  color: themeColors.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: themeColors.primary,
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: themeColors.border,
                  },
                  '&:hover fieldset': {
                    borderColor: themeColors.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: themeColors.primary,
                  },
                },
              }}
            />
          )}
        />

        {/* Search query input */}
        <Tooltip
          title={!isAuthenticated ? 'Please login to use search query' : ''}
          arrow
          placement='top'
          disableHoverListener={isAuthenticated}
          disableFocusListener={isAuthenticated}
          disableTouchListener={isAuthenticated}
        >
          <Box
            component='span'
            sx={{
              display: 'block',
              width: '100%',
              cursor: !isAuthenticated ? 'not-allowed' : 'text',
            }}
          >
            <CustomTextField
              size='small'
              label='Search in Treaty Content'
              value={tempSearchQuery}
              onChange={(e) => {
                setTempSearchQuery(e.target.value)
                setSelectedAutocompleteSuggestion('')
                setShowAutocomplete(true)
              }}
              fullWidth
              margin='normal'
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  !isSearchButtonDisabled &&
                  !isSearchQueryDisabled &&
                  tempSearchQuery.trim()
                ) {
                  handleSearchButtonClick()
                  setShowAutocomplete(false)
                }
              }}
              disabled={isSearchQueryDisabled}
              inputProps={{
                'data-autocomplete-key': autocompleteRequestKey,
                'data-debounced-query': debouncedAutocompleteQuery,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: themeColors.border,
                  },
                  '&:hover fieldset': {
                    borderColor: themeColors.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: themeColors.primary,
                  },
                },
                '& .MuiInputBase-root.Mui-disabled': {
                  cursor: 'not-allowed',
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  cursor: 'not-allowed',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchRoundedIcon color='action' />
                  </InputAdornment>
                ),
                endAdornment: tempSearchQuery ? (
                  <InputAdornment position='end'>
                    {autocompleteLoading && (
                      <CircularProgress
                        size={16}
                        sx={{ color: themeColors.primary, mr: 1 }}
                      />
                    )}
                    <IconButton
                      // size='small'
                      aria-label='clear search'
                      onClick={() => {
                        setTempSearchQuery('')
                        setSelectedAutocompleteSuggestion('')
                        setAutocompleteResults({})
                        setShowAutocomplete(false)
                      }}
                      edge='end'
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        </Tooltip>
        {showAutocomplete && autocompleteItems.length > 0 && (
          <Paper
            elevation={3}
            sx={{
              mt: 0.5,
              maxHeight: 280,
              overflowY: 'auto',
              borderRadius: '8px',
              border: `1px solid ${themeColors.border}`,
            }}
          >
            {autocompleteItems.map((item) => (
              <Box
                key={`${item.mappingType}-${item.value}`}
                onClick={() => {
                  setTempSearchQuery(item.value)
                  setSelectedAutocompleteSuggestion(item.value)
                  setShowAutocomplete(false)
                }}
                sx={{
                  px: 1.5,
                  py: 1.2,
                  cursor: 'pointer',
                  borderBottom: `1px solid ${themeColors.border}`,
                  '&:last-of-type': {
                    borderBottom: 'none',
                  },
                  '&:hover': {
                    bgcolor: '#f6f6f6',
                  },
                }}
              >
                <Typography variant='body2' sx={{ color: themeColors.primary }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
        {!isAuthenticated && (
          <Typography
            variant='caption'
            sx={{
              display: 'block',
              mt: -1,
              mb: 1,
              color: themeColors.primary,
              fontWeight: 600,
            }}
          >
            Login to use search query.
          </Typography>
        )}

        {/* Action buttons */}
        <Box
          display='flex'
          justifyContent='space-between'
          mt={3}
          mb={1}
          sx={{
            gap: 2,
          }}
        >
          <Button
            variant='contained'
            onClick={handleSearchButtonClick}
            startIcon={
              loading ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <SearchRoundedIcon />
              )
            }
            disabled={isSearchButtonDisabled}
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            sx={{
              color: '#fff',
              bgcolor: themeColors.primary,
              fontWeight: 500,
              flexGrow: 1,
              ':hover': {
                color: themeColors.primary,
                bgcolor: themeColors.secondary,
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderRadius: '4px',
            }}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>

          <Button
            variant='outlined'
            onClick={handleResetClick}
            startIcon={<RestartAltRoundedIcon />}
            disabled={loading}
            sx={{
              color: themeColors.primary,
              bgcolor: '#fff',
              borderColor: themeColors.primary,
              fontWeight: 500,
              ':hover': {
                color: themeColors.primary,
                bgcolor: themeColors.secondary,
                borderColor: themeColors.primary,
              },
              borderRadius: '4px',
            }}
          >
            Reset
          </Button>
        </Box>

        {/* Snackbar for notifications - positioned above other elements */}
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={snackbarOpen}
          onClose={handleSnackbarClose}
          autoHideDuration={1500}
          sx={{ zIndex: theme.zIndex.drawer + 2 }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant='filled'
            sx={{
              mt: 5.5,
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              backgroundColor:
                snackbarSeverity === 'warning'
                  ? '#ffcf51'
                  : snackbarSeverity === 'error'
                    ? '#d32f2f'
                    : snackbarSeverity === 'success'
                      ? '#43a047'
                      : '#232536',
              color: snackbarSeverity === 'warning' ? '#232536' : '#fff',
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        {/* Hover Popup for login reminder */}
        <Popover
          open={popoverOpen}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          sx={{
            pointerEvents: 'none',
            '& .MuiPopover-paper': {
              pointerEvents: 'none',
            },
          }}
          disableRestoreFocus
          disableAutoFocus
          disableEnforceFocus
          disableScrollLock
          hideBackdrop
          PaperProps={{
            sx: {
              bgcolor: '#FFDA1B',
              color: '#232536',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              maxWidth: { xs: '250px', sm: '300px' },
              p: { xs: 1.5, sm: 2 },
              mt: 1,
            },
            role: 'tooltip',
            'aria-hidden': 'true',
          }}
        >
          <Typography
            variant='body2'
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              lineHeight: 1.4,
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#232536',
            }}
          >
            Please login to use the portal
          </Typography>
        </Popover>
      </Paper>
    )
  },
)

export default TaxTreatySideBar
